import {eatClubClient} from "@globals/config/apiClient";
import {CheckoutResponse} from "@globals/types/CheckoutResponse";
import {ExtendedError} from "@globals/types/ExtendedError";
import {DateString, ObjectWithAnyProps} from "@globals/types/utilityTypes";

import {
  DaySubsidyInfoData,
  EditDailyItemParams,
  ErrorTitle,
  ExceptionType,
  PartialOrder,
} from "./interfaces";

function errorHandler({response}: ObjectWithAnyProps): ExtendedError {
  const error = {
    title: response?.title,
    status: response?.status,
    message: response?.data?.reason || response?.message,
    addSupportMessage: response?.addSupportMessage,
  };
  throw error;
}

export type CreatePartialOrderParams = {
  date: DateString;
  locationId?: number;
};

export function createPartialOrder({
  date,
  locationId,
}: CreatePartialOrderParams): Promise<PartialOrder> {
  // Returns menu item id as order item id in response.order_items
  return eatClubClient
    .post(`/foodcourt/order/`, {date, location_id: locationId})
    .then((response) => {
      return response.data;
    })
    .catch(errorHandler);
}

export function editOrderItem({
  orderId,
  ...itemData
}: EditDailyItemParams): Promise<PartialOrder> {
  // Returns menu item id as order item id in response.order_items
  return (
    // item_id is the menu item id in the request
    eatClubClient
      .post(`/foodcourt/order/${orderId}/item/${itemData.item_id}/`, itemData)
      .then((response) => {
        if (response.data.code) {
          // if we have a code in the response -> handle it as an error;
          throw response.data;
        } else {
          return response.data;
        }
      })
      .catch((response) => {
        switch (response.code) {
          case ExceptionType.InventoryException:
            response = {
              status: 400,
              title: ErrorTitle.ItemSoldOut,
              message: response.message,
            };
            break;
          case ExceptionType.OrderException:
            response = {
              status: 400,
              title: ErrorTitle.ItemLargeAmount,
              message: response.message,
            };
            break;
          default:
            response = {
              status: 400,
              title: ErrorTitle.ItemGeneric,
              // will be replaced with generic message;
              message: response.message || undefined,
            };
            break;
        }
        errorHandler({response});
      })
  );
}

export function getPartialOrders(): Promise<PartialOrder[]> {
  // Returns menu item id as order item id in response.order_items
  return eatClubClient
    .get("/foodcourt/order/")
    .then((response) => response.data)
    .catch(errorHandler);
}

export function checkout(): Promise<CheckoutResponse> {
  return eatClubClient
    .post("/api/orders/checkout/")
    .then(({data}) => data)
    .catch(({response}) => {
      errorHandler({
        response: {
          status: response.status,
          title: ErrorTitle.CheckoutGeneric,
          message: response?.data?.reason,
          addSupportMessage: true,
        },
      });
    });
}

export function getSubsidyInfo(date: DateString): Promise<DaySubsidyInfoData> {
  return eatClubClient
    .get(`/member/api/subsidy-info/${date}/`)
    .then((response) => response.data)
    .catch(errorHandler);
}
